/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2022
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import TestToolIdentification from './TestToolIdentification';
import TestToolUserCredentials from './TestToolUserCredentials';
import TestToolDigestAuth from './TestToolDigestAuth';
import TestToolHobaAuth from './TestToolHobaAuth';
import TestToolMutualAuth from './TestToolMutualAuth';
import TestToolNegotiateAuth from './TestToolNegotiateAuth';
import TestToolVapidAuth from './TestToolVapidAuth';
import TestToolScramShaAuth from './TestToolScramShaAuth';
import TestToolSecurityFlows from './TestToolSecurityFlows';
import TestToolScopes from './TestToolScopes';
import TestToolAccessCode from './TestToolAccessCode';
import TestToolAccessToken from './TestToolAccessToken';
import TestToolRedirectUri from './TestToolRedirectUri';
import TestToolRefreshToken from './TestToolRefreshToken';
import { get, isEqual } from 'lodash';
import { Accordion, AccordionItem, Select, SelectItem, TextInput } from 'carbon-components-react';
import { TestToolOidcDiscovery } from './TestToolOidcDiscovery';
import { findFirstAppWithAccess } from 'utils/common';
import classNames from 'classnames';

export class TestToolSecurity extends Component {
  constructor(props) {
    super(props);

    this.state = {
      userCredentials: {
        username: null,
        passwd: null
      },
      identification: {
        clientID: null,
        clientSecret: '',
        redirectUri: null,
        redirectUris: [],
        externalApiKeys: {}
      },
      flags: {},
      removeFields: false,
      scopes: [],
      authToken: {
        code: '',
        access_token: ''
      },
      httpBearerToken: '',
      httpDigestAuth: {
        digestUsername: null,
        digestRealm: '',
        digestNonce: '',
        digestOpaque: '',
        digestResponse: ''
      },
      httpHobaAuth: {
        hobaKeyIdentifier: '',
        hobaChallenge: '',
        hobaNonce: '',
        hobaSignature: ''
      },
      httpMutualAuth: {
        mutualVersion: '',
        mutualSid: '',
        mutualNonce: '',
        mutualVkc: ''
      },
      httpNegotiateAuth: {
        negotiateGssapiData: ''
      },
      httpOAuthAuth: {
        oauthRealm: '',
        oauthConsumerKey: '',
        oauthToken: '',
        oauthSignatureMethod: '',
        oauthSignature: '',
        oauthTimestamp: '',
        oauthNonce: '',
        oauthVersion: ''
      },
      httpVapidAuth: {
        vapidJSONWebToken: '',
        vapidJSONWebKey: ''
      },
      httpScramShaAuth: {
        scramSessionId: '',
        scramEncryptedCreds: ''
      }
    };

    this.changeIdentificationHandler = this.changeIdentificationHandler.bind(this);
    this.changeUserCredentialsHandler = this.changeUserCredentialsHandler.bind(this);
    this.changeSecurityFlow = this.changeSecurityFlow.bind(this);
    this.changeSecurityOauthFlow = this.changeSecurityOauthFlow.bind(this);
    this.changeScopes = this.changeScopes.bind(this);
    this.handleAuthToken = this.handleAuthToken.bind(this);
    this.handleHttpBearerChange = this.handleHttpBearerChange.bind(this);
    this.changeHttpDigestAuthHandler = this.changeHttpDigestAuthHandler.bind(this);
    this.changeHttpHobaAuthHandler = this.changeHttpHobaAuthHandler.bind(this);
    this.changeHttpMutualAuthHandler = this.changeHttpMutualAuthHandler.bind(this);
    this.changeHttpNegotiateAuthHandler = this.changeHttpNegotiateAuthHandler.bind(this);
    this.changeHttpOAuthAuthHandler = this.changeHttpOAuthAuthHandler.bind(this);
    this.changeHttpVapidAuthHandler = this.changeHttpVapidAuthHandler.bind(this);
    this.changeHttpScramShaAuthHandler = this.changeHttpScramShaAuthHandler.bind(this);
    this.changeAuthorizationUrl = this.changeAuthorizationUrl.bind(this);
    this.changeTokenUrl = this.changeTokenUrl.bind(this);
    this.changeRefreshUrl = this.changeRefreshUrl.bind(this);
  }

  changeAuthorizationUrl(evt) {
    this.setState({ flags: Object.assign(this.state.flags, { selectedAuthUrl: get(evt, 'target.value') }) });
  }
  changeTokenUrl(evt) {
    this.setState({ flags: Object.assign(this.state.flags, { selectedTokenUrl: get(evt, 'target.value') }) });
  }
  changeRefreshUrl(evt) {
    this.setState({ flags: Object.assign(this.state.flags, { selectedRefreshUrl: get(evt, 'target.value') }) });
  }

  componentDidMount() {
    this.configure(this.props);
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps, this.props)) {
      this.configure(this.props);
    }
  }

  configure(props) {
    let newState = Object.assign({}, this.state);
    let initState = false;

    if (props.config) {
      if (props.config.flags) {
        initState = true;
        if (!this.state.flags || Object.keys(this.state.flags).length === 0 || this.state.endpoint !== props.endpoint) {
          newState.flags = props.config.flags;
        }
      }

      if (newState.flags.oauthAuthUrl) {
        newState.flags.selectedAuthUrl = newState.flags.oauthAuthUrl;
      }
      if (newState.flags.oauthTokenUrl) {
        newState.flags.selectedTokenUrl = newState.flags.oauthTokenUrl;
      }
      if (newState.flags.oauthRefreshUrl) {
        newState.flags.selectedRefreshUrl = newState.flags.oauthRefreshUrl;
      }
      if (Array.isArray(newState.flags.selectedAuthUrl) && newState.flags.selectedAuthUrl.length > 0) {
        newState.flags.selectedAuthUrl = newState.flags.selectedAuthUrl[0];
      }
      if (Array.isArray(newState.flags.selectedTokenUrl) && newState.flags.selectedTokenUrl.length > 0) {
        newState.flags.selectedTokenUrl = newState.flags.selectedTokenUrl[0];
      }
      if (Array.isArray(newState.flags.selectedRefreshUrl) && newState.flags.selectedRefreshUrl.length > 0) {
        newState.flags.selectedRefreshUrl = newState.flags.selectedRefreshUrl[0];
      }
      if (
        ((newState.flags.oauthFlow === 'accessCode' || newState.flags.oauthFlow === 'authorizationCode' || newState.flags.oauthFlow === 'implicit') &&
          props.options.optimiseOauthUX) ||
        newState.flags.openIdConnectUrl
      ) {
        newState.removeFields = true;
      } else {
        newState.removeFields = false;
      }

      if (props.endpoint !== this.state.endpoint) {
        newState.endpoint = props.endpoint;
      }

      if (!this.state.currentFlow || newState.flags !== this.state.flags) {
        let initialFlow = Object.keys(props.config.securityFlows)[0];
        newState.currentFlow = initialFlow;
      }
    }

    if (!this.state.identification.clientID && props.apps && props.apps.length > 0 && props.apps[0].credentials && props.apps[0].credentials.length > 0) {
      let initialIdentification = {};

      let initialApp = findFirstAppWithAccess(this.props.apps);
      initialIdentification.clientID = initialApp ? initialApp.credentials[0]['client_id'] : this.props.apps[0].credentials[0]['client_id'];
      initialIdentification.redirectUris = [''];

      let initialRedirectUri = initialApp ? initialApp.redirectUri : this.props.apps[0].redirectUri;
      if (typeof initialRedirectUri === 'string') {
        initialIdentification.redirectUris[0] = initialRedirectUri;
      } else if (initialRedirectUri && initialRedirectUri.length > 0) {
        initialIdentification.redirectUris = initialRedirectUri;
      }
      initialIdentification.redirectUri = initialIdentification.redirectUris[0];

      initialIdentification.externalApiKeys = {};

      initState = true;
      newState.identification = initialIdentification;
    }

    if (!this.state.userCredentials.username && props.options.authentication) {
      let initialUserCreds = {};

      initialUserCreds.username = this.props.options.authentication.username ? this.props.options.authentication.username : null;
      initialUserCreds.passwd = this.props.options.authentication.password ? this.props.options.authentication.password : null;

      newState.userCredentials = initialUserCreds;
    }

    this.setState(newState);
    if (initState && this.props.onChange && typeof this.props.onChange === 'function') {
      this.props.onChange(newState);
    }
  }

  changeIdentificationHandler(update) {
    let newCreds = Object.assign(this.state.identification, update);
    let newState = Object.assign(this.state, { identification: newCreds });

    this.setState(newState);
    if (this.props.onChange && typeof this.props.onChange === 'function') {
      this.props.onChange(newState);
    }
  }

  changeHttpDigestAuthHandler(update) {
    let newCreds = Object.assign(this.state.httpDigestAuth, update);
    let newState = Object.assign(this.state, { httpDigestAuth: newCreds });

    this.setState(newState);
    if (this.props.onChange && typeof this.props.onChange === 'function') {
      this.props.onChange(newState);
    }
  }

  changeHttpHobaAuthHandler(update) {
    let newCreds = Object.assign(this.state.httpHobaAuth, update);
    let newState = Object.assign(this.state, { httpHobaAuth: newCreds });

    this.setState(newState);
    if (this.props.onChange && typeof this.props.onChange === 'function') {
      this.props.onChange(newState);
    }
  }

  changeHttpMutualAuthHandler(update) {
    let newCreds = Object.assign(this.state.httpMutualAuth, update);
    let newState = Object.assign(this.state, { httpMutualAuth: newCreds });

    this.setState(newState);
    if (this.props.onChange && typeof this.props.onChange === 'function') {
      this.props.onChange(newState);
    }
  }

  changeHttpNegotiateAuthHandler(update) {
    let newCreds = Object.assign(this.state.httpNegotiateAuth, update);
    let newState = Object.assign(this.state, { httpNegotiateAuth: newCreds });

    this.setState(newState);
    if (this.props.onChange && typeof this.props.onChange === 'function') {
      this.props.onChange(newState);
    }
  }

  changeHttpOAuthAuthHandler(update) {
    let newCreds = Object.assign(this.state.httpOAuthAuth, update);
    let newState = Object.assign(this.state, { httpOAuthAuth: newCreds });

    this.setState(newState);
    if (this.props.onChange && typeof this.props.onChange === 'function') {
      this.props.onChange(newState);
    }
  }

  changeHttpVapidAuthHandler(update) {
    let newCreds = Object.assign(this.state.httpVapidAuth, update);
    let newState = Object.assign(this.state, { httpVapidAuth: newCreds });

    this.setState(newState);
    if (this.props.onChange && typeof this.props.onChange === 'function') {
      this.props.onChange(newState);
    }
  }

  changeHttpScramShaAuthHandler(update) {
    let newCreds = Object.assign(this.state.httpScramShaAuth, update);
    let newState = Object.assign(this.state, { httpScramShaAuth: newCreds });

    this.setState(newState);
    if (this.props.onChange && typeof this.props.onChange === 'function') {
      this.props.onChange(newState);
    }
  }

  changeUserCredentialsHandler(update) {
    let newCreds = Object.assign(this.state.userCredentials, update);
    let newState = Object.assign(this.state, { userCredential: newCreds });

    this.setState(newState);
    if (this.props.onChange && typeof this.props.onChange === 'function') {
      this.props.onChange(newState);
    }
  }

  changeSecurityFlow(flow) {
    let selectedFlags = this.props.config.securityFlows[flow].config;

    let newState = Object.assign(this.state, { flags: selectedFlags, currentFlow: flow });
    newState.flags.selectedAuthUrl = selectedFlags.oauthAuthUrl;
    newState.flags.selectedTokenUrl = selectedFlags.oauthTokenUrl;
    newState.flags.selectedRefreshUrl = selectedFlags.oauthRefreshUrl;
    if (Array.isArray(newState.flags.selectedAuthUrl) && newState.flags.selectedAuthUrl.length > 0) {
      newState.flags.selectedAuthUrl = newState.flags.selectedAuthUrl[0];
    }
    if (Array.isArray(newState.flags.selectedTokenUrl) && newState.flags.selectedTokenUrl.length > 0) {
      newState.flags.selectedTokenUrl = newState.flags.selectedTokenUrl[0];
    }
    if (Array.isArray(newState.flags.selectedRefreshUrl) && newState.flags.selectedRefreshUrl.length > 0) {
      newState.flags.selectedRefreshUrl = newState.flags.selectedRefreshUrl[0];
    }

    if (
      ((newState.flags.oauthFlow === 'accessCode' || newState.flags.oauthFlow === 'authorizationCode' || newState.flags.oauthFlow === 'implicit') &&
        this.props.options.optimiseOauthUX) ||
      newState.flags.openIdConnectUrl
    ) {
      newState.removeFields = true;
    } else {
      newState.removeFields = false;
    }

    this.setState(newState);
    if (this.props.onChange && typeof this.props.onChange === 'function') {
      this.props.onChange(newState);
    }
  }

  changeSecurityOauthFlow(oauthFlow) {
    let selectedFlags = this.props.config.securityFlows[this.state.currentFlow].config.oauthFlowsConfig[oauthFlow];
    selectedFlags.oauthFlowsConfig = this.props.config.securityFlows[this.state.currentFlow].config.oauthFlowsConfig;

    let newState = Object.assign(this.state, { flags: selectedFlags });
    this.setState(newState);
    if (this.props.onChange && typeof this.props.onChange === 'function') {
      this.props.onChange(newState);
    }
  }

  changeScopes(scopes) {
    let newState = Object.assign(this.state, { scopes: scopes });
    this.setState(newState);
    if (this.props.onChange && typeof this.props.onChange === 'function') {
      this.props.onChange(newState);
    }
  }

  handleAuthToken(authToken) {
    let newToken = Object.assign(this.state.authToken, authToken);
    let newState = Object.assign(this.state, { authToken: newToken });

    this.setState(newState);
    if (this.props.onChange && typeof this.props.onChange === 'function') {
      this.props.onChange(newState);
    }
  }

  handleHttpBearerChange(event) {
    this.setState({ httpBearerToken: event.target.value });
    if (this.props.onChange && typeof this.props.onChange === 'function') {
      this.props.onChange({ httpBearerToken: event.target.value });
    }
  }

  render() {
    let { config, options, apps, t, isEnforced } = this.props;
    let {
      flags,
      identification,
      userCredentials,
      authToken,
      httpBearerToken,
      httpDigestAuth,
      httpMutualAuth,
      httpNegotiateAuth,
      httpHobaAuth,
      httpVapidAuth,
      httpScramShaAuth,
      removeFields,
      currentFlow
    } = this.state;
    var shouldDisplay =
      (flags && (flags.requiresIdentificationSection || flags.requiresAuthorizationSection)) ||
      (config && config.securityFlows && Object.keys(config.securityFlows).length > 1);
    let stateToPass = Object.assign({}, this.state);

    if (shouldDisplay) {
      return (
        <div className="testToolSection">
          <div className="labelText">{t('security')}</div>
          <div className="testToolSecurity">
            {config && config.securityFlows && Object.keys(config.securityFlows).length > 1 && (
              <div className="securityFlow">
                <TestToolSecurityFlows flows={config.securityFlows} onChange={this.changeSecurityFlow} t={t} currentFlow={currentFlow} />
              </div>
            )}

            {flags.requiresIdentificationSection && (
              <TestToolIdentification
                flags={flags}
                options={options}
                externalApiKeys={identification.externalApiKeys}
                apps={apps}
                onChange={this.changeIdentificationHandler}
                t={t}
                isEnforced={isEnforced}
                onCopyClientIdCallback={() => navigator.clipboard.writeText(identification.clientID)}
              />
            )}
            {flags.requiresAuthorizationSection && (
              <Accordion className="testToolAuthorization" align={'start'}>
                <AccordionItem className="testToolAuthorizationInput" title={t('authorization')} open={true}>
                  {flags.oauthFlowsConfig && Object.keys(flags.oauthFlowsConfig).length > 1 && (
                    <div className="securityFlow">
                      <TestToolSecurityFlows flows={flags.oauthFlowsConfig} onChange={this.changeSecurityOauthFlow} t={t} />
                    </div>
                  )}
                  {flags.requiresUserCredentials && <TestToolUserCredentials userCreds={userCredentials} onChange={this.changeUserCredentialsHandler} t={t} />}
                  {flags.requiresDigestAuth && <TestToolDigestAuth digestCreds={httpDigestAuth} onChange={this.changeHttpDigestAuthHandler} t={t} />}
                  {flags.requiresHobaAuth && <TestToolHobaAuth hobaCreds={httpHobaAuth} onChange={this.changeHttpHobaAuthHandler} t={t} />}
                  {flags.requiresMutualAuth && <TestToolMutualAuth mutualCreds={httpMutualAuth} onChange={this.changeHttpMutualAuthHandler} t={t} />}
                  {flags.requiresNegotiateAuth && (
                    <TestToolNegotiateAuth negotiateCreds={httpNegotiateAuth} onChange={this.changeHttpNegotiateAuthHandler} t={t} />
                  )}
                  {flags.requiresVapidAuth && <TestToolVapidAuth vapidCreds={httpVapidAuth} onChange={this.changeHttpVapidAuthHandler} t={t} />}
                  {flags.requiresScramShaAuth && <TestToolScramShaAuth scramCreds={httpScramShaAuth} onChange={this.changeHttpScramShaAuthHandler} t={t} />}

                  {flags.oauthScopes && flags.oauthScopes.length > 0 && !removeFields && (
                    <TestToolScopes scopes={flags.oauthScopes} onChange={this.changeScopes} t={t} />
                  )}

                  {flags.requiresRedirectUri && !removeFields && <TestToolRedirectUri value={identification} onChange={this.changeIdentificationHandler} />}
                  {flags.oauthAuthUrl && flags.oauthAuthUrl.length > 0 && (
                    <div className="url authUrl">
                      <legend htmlFor="authUrl" className="parameterName">
                        {t('explorer_authorization_url')}
                      </legend>
                      {Array.isArray(flags.oauthAuthUrl) && flags.oauthAuthUrl.length > 1 && (
                        <Select
                          hideLabel
                          data-testid="oauthAuthUrl-selector"
                          id="oauthAuthUrl-select"
                          value={flags.selectedAuthUrl}
                          onChange={this.changeAuthorizationUrl}
                        >
                          {flags.oauthAuthUrl.map((url, index) => {
                            return <SelectItem data-testid="oauthAuthUrl-item" key={index} value={url} text={url} />;
                          })}
                        </Select>
                      )}
                      {(!Array.isArray(flags.oauthAuthUrl) || flags.oauthAuthUrl.length === 1) && <p className="parameterValue">{flags.selectedAuthUrl}</p>}
                    </div>
                  )}
                  {flags.oauthTokenUrl && (
                    <div className="url tokenUrl">
                      <legend htmlFor="token-url" className="parameterName">
                        {t('explorer_token_url')}
                      </legend>
                      {Array.isArray(flags.oauthTokenUrl) && flags.oauthTokenUrl.length > 1 && (
                        <Select
                          hideLabel
                          data-testid="oauthTokenUrl-selector"
                          id="oauthTokenUrl-select"
                          value={flags.selectedTokenUrl}
                          onChange={this.changeTokenUrl}
                        >
                          {flags.oauthTokenUrl.map((url, index) => {
                            return <SelectItem data-testid="oauthTokenUrl-item" key={index} value={url} text={url} />;
                          })}
                        </Select>
                      )}
                      {(!Array.isArray(flags.oauthTokenUrl) || flags.oauthTokenUrl.length === 1) && <p className="parameterValue">{flags.selectedTokenUrl}</p>}
                    </div>
                  )}
                  {flags.oauthRefreshUrl && (
                    <div className="url refreshUrl">
                      <legend htmlFor="refreshurl" className="parameterName">
                        {t('explorer_refresh_url')}
                      </legend>
                      {Array.isArray(flags.oauthRefreshUrl) && flags.oauthRefreshUrl.length > 1 && (
                        <Select
                          hideLabel
                          data-testid="oauthRefreshUrl-selector"
                          id="oauthRefreshUrl-select"
                          value={flags.selectedRefreshUrl}
                          onChange={this.changeRefreshUrl}
                        >
                          {flags.oauthRefreshUrl.map((url, index) => {
                            return <SelectItem data-testid="oauthRefreshUrl-item" key={index} value={url} text={url} />;
                          })}
                        </Select>
                      )}
                      {(!Array.isArray(flags.oauthRefreshUrl) || flags.oauthRefreshUrl.length === 1) && (
                        <p className="parameterValue">{flags.selectedRefreshUrl}</p>
                      )}
                    </div>
                  )}

                  {flags.openIdConnectUrl && (
                    <>
                      <TestToolOidcDiscovery config={stateToPass} t={t} />
                      <TestToolAccessToken config={stateToPass} onChange={this.handleAuthToken} />
                    </>
                  )}

                  {flags.requiresOauth && (
                    <div className={classNames('testToolOauth', { oauthFieldsRemoved: removeFields })}>
                      {(flags.oauthFlow === 'accessCode' || flags.oauthFlow === 'authorizationCode') && !removeFields && (
                        <TestToolAccessCode config={stateToPass} onChange={this.handleAuthToken} />
                      )}
                      <TestToolAccessToken config={stateToPass} onChange={this.handleAuthToken} />
                      {flags.requiresRefreshToken && authToken.refresh_token && <TestToolRefreshToken config={stateToPass} onClick={this.handleAuthToken} />}
                    </div>
                  )}

                  {flags.requiresHttpBearerToken && (
                    <div className="httpBearerToken" key="httpBearer">
                      <TextInput id="bearer" name="bearer" type="input" labelText="Bearer" onChange={this.handleHttpBearerChange} value={httpBearerToken} />
                    </div>
                  )}
                </AccordionItem>
              </Accordion>
            )}
          </div>
        </div>
      );
    } else {
      return null;
    }
  }
}

export default withTranslation()(TestToolSecurity);
